import React, { useReducer, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { ChartEntryFilterRow } from './ChartEntryFilterRow';
import { ChartEntryMetadata } from '@decisions/server/DecisionsFramework/Design/Charts/ChartEntryMetadata';
import './ChartEntryFilter.less';
import { ChartMetadataBehavior } from '@decisions/server/DecisionsFramework/Design/Charts/ChartMetadataBehavior';
import { Label } from 'components/inputs/Label';
import {
  IPersonalizationScopeSelectorProps,
  PersonalizationScopeSelector,
} from 'components/Editors/data-filters/PersonalizationScopeSelector';
import { IconButton } from 'components/buttons/IconButton';
import { Callout } from 'components/layout/Callout';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import {
  chartEntryFilterReducer,
  EntryFilterActionTypes,
  getEntriesByKey,
  IChartEntryFilterState,
} from './chartEntryFilterReducer';

interface IChartEntryFilterProps {
  title: string;
  // behavior: ChartMetadataBehavior;
  entries: ChartEntryMetadata[];
  /** Derived from data, not edited by the user */
  originals: ChartEntryMetadata[];
  onUpdateSet: (entries: ChartEntryMetadata[]) => void;
  className?: string;
  personalization?: IPersonalizationScopeSelectorProps;
  canConfigureColors?: boolean;
}

const getClassName = () => classNames('dp-chart-entry-filter');

const getMultiSelectClassName = (multiSelecting: boolean) =>
  classNames('dp-chart-entry-filter__multiselect-icon', {
    'dp-chart-entry-filter__multiselect-icon--active': multiSelecting,
  });

const CHART_ENTRY_FILTER_STATE: IChartEntryFilterState = {
  selectedRows: [],
  entries: [],
  originalEntries: new Map(),
  multiSelect: false,
  multiSelectActions: false,
};

/**
 * A Component for manipulating data on it's way to being displayed on a chart.
 *
 * 1. Label
 * 1. Color
 * 1. Visible
 * 1. Sorting
 *
 * Additionally adding/grouping entries which may or may not yet be present in the data
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ChartEntryFilter({
  canConfigureColors,
  entries,
  originals,
  onUpdateSet,
  personalization,
  title,
}: IChartEntryFilterProps) {
  const [state, dispatch] = useReducer(chartEntryFilterReducer, {
    // reducer will not fire on first render
    ...CHART_ENTRY_FILTER_STATE,
    entries,
    originalEntries: getEntriesByKey(originals), // keep track of what we first got from the server
  });
  const multiRef: React.MutableRefObject<HTMLButtonElement | null> = useRef(null);
  const isSeries = entries[0]?.Behavior === ChartMetadataBehavior.Series;
  const onRowSelection = (index: number, isMulti: boolean) => {
    if (isSeries) {
      return; // not currently allowing grouping of series, only labels
    }
    dispatch({ type: 'SelectRow', index, isMulti });
  };
  const onMoveUp = (index: number) => dispatch({ index, type: 'Move Up' });
  const onMoveDown = (index: number) => dispatch({ index, type: 'Move Down' });
  const toggleVisible = (index: number) => dispatch({ index, type: 'ToggleVisibility' });
  const onRemove = (index: number) => dispatch({ type: 'Remove', index });
  const onLabelChange = (index: number, label: string) => dispatch({ type: 'Update Label', index, label });
  const onUnGroupChild = (groupKey: string, index: number) => dispatch({ type: 'Un Group', groupKey, index });
  const onMultiSelect = () => {
    dispatch({ type: 'Toggle Multi Select' });
  };
  const onAddClick = () => dispatch({ type: 'Add Row' });
  const onMultiAction = (type: EntryFilterActionTypes) => dispatch({ type });
  // Allowing color change for series here. Others rows will map to "User Defined Colors"
  const onColorChange = canConfigureColors
    ? (index: number, color: string) => dispatch({ type: 'Update Color', index, color })
    : undefined;
  const onAddCancel = () => dispatch({ type: 'Cancel Add Row' });

  const stateEntries = state.entries;
  const entriesString = getEntryString(state.entries);
  const prevEntriesString = useRef(entriesString);

  // // FIXME how could we actually end up here? The original set shouldn't realistically need to replace what's here.
  // // updates from upstream:
  // useEffect(() => {
  //   // these editors are not interdependent with other PGrid values.
  //   dispatch({ type: 'Set Entries', entries });
  //   // Using a string rep of the list here as a simplistic way to avoid update loops.
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [entriesString]);

  useEffect(() => {
    if (entriesString !== prevEntriesString.current) {
      onUpdateSet(stateEntries);
      prevEntriesString.current = entriesString;
    }
    // we want this to emit after actual changes, not initial load, etc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entriesString]);

  return (
    <Label
      label={title}
      doNotWrap={true}
      className={getClassName()}
      actions={
        <>
          {!isSeries && (
            <>
              <IconButton
                iconClassName={getMultiSelectClassName(state.multiSelect)}
                onClick={onMultiSelect}
                // iconSize="normal"
                ref={multiRef}
              />
              <Callout
                sourceRef={multiRef}
                isOpen={state.multiSelectActions}
                onClose={onMultiSelect}
                showArrow={true}
                placement="top"
                flipBehavior={['top', 'left', 'bottom', 'right']}
              >
                <ActionLinks
                  callback={onMultiAction}
                  values={{
                    current: [
                      { label: 'Group', value: 'Group', disabled: state.selectedRows.length < 2 },
                      { label: 'Cancel', value: 'Cancel Multi Select' },
                    ],
                  }}
                />
              </Callout>
            </>
          )}
          <IconButton iconClassName="dp-chart-entry-filter__add-icon" onClick={onAddClick} iconSize="small" />
          {personalization && <PersonalizationScopeSelector {...{ ...personalization, small: true, partOnly: true }} />}
        </>
      }
    >
      {state.entries.map((entry, i) => (
        <ChartEntryFilterRow
          key={entry.Key}
          entry={entry}
          index={i}
          onSelection={onRowSelection}
          onLabelChange={onLabelChange}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onToggleVisible={toggleVisible}
          onRemove={onRemove}
          selected={state.selectedRows.includes(i)}
          unGroupChild={onUnGroupChild}
          onAddCancel={onAddCancel}
          onColorChange={onColorChange}
          multiSelecting={state.multiSelect}
          isFirst={i === 0}
          isLast={i === state.entries.length - 1}
        />
      ))}
    </Label>
  );
}

function getEntryString(entries: ChartEntryMetadata[]) {
  // we want this to track as "different" if any ordering/label/color/visibility  changes occur
  return entries.map(({ LabelText, Color, Visible }) => `${LabelText}-${Color}-${Visible}`).join('\n');
}
